import React, { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetProjectFeeDetails } from "../../../../../api/services/project/projectApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
} from "../../../../../components";
import { ExpandCollapseButton } from "../../../../../components/expandCollapseButton/ExpandCollapseButton";
import { TaxDetails } from "../../../../../components/taxDetails/TaxDetails";
import {
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../../components/textFieldCurrency/CurrencyUtils";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import {
  getValueOrBlank,
  isMilestoneBilling,
} from "../../../../../helpers/utils";
import useAccordion from "../../../../../hooks/useAccordion";
import { useForceUpdate } from "../../../../../hooks/useForceUpdate";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { createClientSelector } from "../../../../../redux/slices/client/createClientSelector";
import {
  billingConfigurationDetailsFormKeys,
  taxDetailsFormKeys,
} from "../../../../createProject/modelsAndSchemas/Models";
import { pageId } from "../../../../createUser/CreateUser";
import { ViewProjectFee } from "../../viewProjectFee/ViewProjectFee";
import { ViewProjectFeeContainer } from "./ViewProjectFeeTab.style";
import { MODULE_PERMISSIONS } from "../../../../../helpers/constants";
import PermissionWrapper from "../../../../../components/permissionWrapper/PermissionWrapper";

const initialAccordionStatus = {
  projectFee: true,
  taxDetails: false,
};

const ViewProjectFeeTab: React.FC = () => {
  const taxDetailsRef = useRef<{
    resetData: () => void;
  }>(null);

  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);

  const { fetchComponentTitle } = useSiteCore();

  const forceUpdate = useForceUpdate();

  const { id: clientId } = useSelector(createClientSelector);
  const { id: projectId } = useParams();

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={labelKey} />;
  };

  const methods = useForm({
    mode: "all",
  });

  const {
    data: projectFeeDetailsData,
    refetchWithParams: getProjectFeeDetails,
  } = useGetProjectFeeDetails();

  const currencyFormat: CurrencyFormat =
    CURRENCY_FORMAT_MAP[getValueOrBlank(projectFeeDetailsData?.currency.name)];

  const { setValue } = methods;

  useEffect(() => {
    if (!projectFeeDetailsData) return;
    const billingSchedules = isMilestoneBilling(
      projectFeeDetailsData?.billingTypeId,
    )
      ? projectFeeDetailsData.billingSchedules?.map((schedule) => ({
          ...schedule,
          activities: schedule.topics,
        }))
      : projectFeeDetailsData.billingSchedules;
    setValue(
      taxDetailsFormKeys.taxEntriesKey,
      projectFeeDetailsData?.taxes || [],
    );

    setValue(taxDetailsFormKeys.programFeeKey, projectFeeDetailsData?.fee);
    setValue(
      billingConfigurationDetailsFormKeys.billingTypeIdKey,
      projectFeeDetailsData?.billingTypeId,
    );
    setValue(
      billingConfigurationDetailsFormKeys.feePaymentTypeKey,
      projectFeeDetailsData?.feePaymentType,
    );
    setValue(
      billingConfigurationDetailsFormKeys.billingSchedulesKey,
      billingSchedules,
    );
    setValue(
      billingConfigurationDetailsFormKeys.schedulePeriodKey,
      projectFeeDetailsData?.schedulePeriod,
    );
  }, [projectFeeDetailsData]);

  useEffect(() => {
    if (projectId) getProjectFeeDetails(projectId);
  }, [projectId]);

  return (
    <FormProvider {...methods}>
      <MuiBox>
        <ExpandCollapseButton
          accordionStatus={accordionStatus}
          handleExpandCollapseAllChange={handleExpandCollapseAllChange}
        />

        <ViewProjectFeeContainer>
          <PermissionWrapper
            permission={MODULE_PERMISSIONS.PROJECT_VIEW_PROJECT_FEE}
          >
            <AccordionHeader
              wrapperClassName="accordion view-project-fee-wrapper"
              title={<AccordionHeaderText label={"Project Fee Details"} />}
              onChange={() => handleIndividualAccordionChange("projectFee")}
              isExpanded={accordionStatus.projectFee}
            >
              <ViewProjectFee
                pageId=""
                programTemplateId={""}
                clientId={clientId}
                projectFeeDetailsData={projectFeeDetailsData}
              />
            </AccordionHeader>
          </PermissionWrapper>

          <PermissionWrapper
            permission={MODULE_PERMISSIONS.PROJECT_VIEW_TAX_DETAILS}
          >
            <AccordionHeader
              wrapperClassName="participant-accordion"
              title={renderHeader(
                fetchComponentTitle(commonSiteCoreKeys.taxDetails),
              )}
              onChange={() => handleIndividualAccordionChange("taxDetails")}
              isExpanded={accordionStatus.taxDetails}
            >
              <TaxDetails
                ref={taxDetailsRef}
                pageId={pageId}
                currencyFormat={currencyFormat}
                currencyName={getValueOrBlank(
                  projectFeeDetailsData?.currency.name,
                )}
                forceUpdate={forceUpdate}
                currencyId={getValueOrBlank(projectFeeDetailsData?.currency.id)}
                isInEditMode={false}
                isInViewMode={true}
                taxDetailsFormKeys={taxDetailsFormKeys}
                programOrProjectFee={
                  projectFeeDetailsData?.fee?.toString() || ""
                }
              />
            </AccordionHeader>
          </PermissionWrapper>
        </ViewProjectFeeContainer>
      </MuiBox>
    </FormProvider>
  );
};

export default ViewProjectFeeTab;
